
import React, { useState, useEffect, useRef } from 'react';
import axios from './utils/axiosConfig';
import { Table, Form, Container, Row, Col, Card, Badge, InputGroup } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from './context/AuthContext'; // 假設你的 AuthContext 位於這個路徑
import { hasPermission } from './utils/permissions'; // 假設你的權限檢查函數位於 utils 目錄中
import LazyLoad from 'react-lazyload';
import { MEMBER_TERM } from './config/clubTerms';

const AllEmployeeTable = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'e_id', direction: 'asc' });
  const [filter, setFilter] = useState('');
  const [storeFilter, setStoreFilter] = useState(''); // 社別過濾
  const [hasMore, setHasMore] = useState(true); // 控制是否還有更多資料
  const [totalRecords, setTotalRecords] = useState(0); // 總記錄數
  const [storeStats, setStoreStats] = useState({}); // 各社統計
  const [allStores, setAllStores] = useState([]); // 所有社別列表
  const observer = useRef(null);
  const { user } = useAuth(); // 取得使用者資訊
  const token = localStorage.getItem('token');
  //console.log('Token:', token);

  const fetchEmployees = async (page, sortKey = 'e_id', sortDirection = 'asc', filter = '', storeFilter = '') => {
    setLoading(true);
    try {
      const response = await axios.get('/Allemployees', {
        params: { 
          page, 
          sortKey, 
          sortDirection, 
          filter, 
          store_name: storeFilter,
          limit: 50 
        },
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const { data, hasMore, totalRecords } = response.data;

      //console.log('Employees data:', data);
      //console.log('Total records:', totalRecords, 'Has more:', hasMore);

      if (page === 1) {
        setEmployees(data);
        setTotalRecords(totalRecords);
        
        // 計算各社統計
        const stats = {};
        const stores = new Set();
        data.forEach(emp => {
          const storeName = emp.store_name || '未知';
          stats[storeName] = (stats[storeName] || 0) + 1;
          if (emp.store_name) {
            stores.add(emp.store_name);
          }
        });
        setStoreStats(stats);
        
        // 更新社別列表（只在第一頁時更新）
        if (!storeFilter) {
          setAllStores(Array.from(stores).sort());
        }
      } else {
        setEmployees((prevEmployees) => [...prevEmployees, ...data]);
      }

      setHasMore(hasMore); // 確認是否還有更多資料
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
    setLoading(false);
  };

  // 當 filter、storeFilter 或 sortConfig 改變時，重置頁碼並重新查詢
  useEffect(() => {
    setPage(1);
    setEmployees([]); // 清空舊資料，避免閃爍
    fetchEmployees(1, sortConfig.key, sortConfig.direction, filter, storeFilter);
  }, [filter, storeFilter, sortConfig.key, sortConfig.direction]);

  // 獲取所有社別列表
  const fetchAllStores = async () => {
    try {
      const response = await axios.get('/Allemployees', {
        params: { allData: 'true' },
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const stores = new Set();
      response.data.data.forEach(emp => {
        if (emp.store_name) {
          stores.add(emp.store_name);
        }
      });
      setAllStores(Array.from(stores).sort());
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  // 只在頁碼改變且頁碼大於 1 時才載入更多資料（分頁加載）
  useEffect(() => {
    if (page > 1) {
      fetchEmployees(page, sortConfig.key, sortConfig.direction, filter, storeFilter);
    }
  }, [page]);

  // 初始載入時獲取所有社別
  useEffect(() => {
    fetchAllStores();
  }, []);

  const lastEmployeeRef = useRef();
  useEffect(() => {
    if (loading || !hasMore) return; // 當正在加載或沒有更多資料時，直接返回

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        //console.log('Load more data...');
        setPage((prevPage) => prevPage + 1);
      }
    });

    if (lastEmployeeRef.current) {
      observer.current.observe(lastEmployeeRef.current);
    }
  }, [loading, hasMore, employees.length]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(1); // 當排序改變時，重置為第一頁
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setPage(1); // 當過濾條件改變時，重置為第一頁
  };

  const handleStoreFilterChange = (e) => {
    setStoreFilter(e.target.value);
    setPage(1); // 當社別過濾條件改變時，重置為第一頁
  };

  const isValidUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // 檢測是否為手機設備
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 自定義樣式
  const customStyles = {
    pageContainer: {
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      paddingTop: isMobile ? '1rem' : '2rem',
      paddingBottom: isMobile ? '1rem' : '2rem',
      paddingLeft: isMobile ? '0.5rem' : '15px',
      paddingRight: isMobile ? '0.5rem' : '15px'
    },
    headerCard: {
      background: 'linear-gradient(135deg, #0066cc 0%, #004499 100%)',
      color: 'white',
      borderRadius: isMobile ? '10px' : '15px',
      border: 'none',
      boxShadow: '0 8px 25px rgba(0, 102, 204, 0.15)'
    },
    statsCard: {
      borderRadius: isMobile ? '8px' : '12px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
      transition: 'transform 0.2s ease-in-out',
      backgroundColor: '#ffffff',
      marginBottom: isMobile ? '0.75rem' : '1rem'
    },
    searchInput: {
      borderRadius: isMobile ? '15px' : '25px',
      border: '2px solid #e2e8f0',
      fontSize: isMobile ? '16px' : '18px',
      transition: 'all 0.3s ease'
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: isMobile ? '10px' : '15px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      overflow: 'hidden'
    },
    tableHeader: {
      backgroundColor: '#0066cc',
      color: 'white',
      fontWeight: '600',
      fontSize: isMobile ? '14px' : '16px',
      borderBottom: 'none'
    },
    tableRow: {
      borderBottom: '1px solid #f1f5f9',
      transition: 'background-color 0.2s ease'
    },
    employeePhoto: {
      width: isMobile ? '120px' : '80px',
      height: isMobile ? '144px' : '96px',
      borderRadius: '8px',
      objectFit: 'cover',
      border: '2px solid #e2e8f0'
    },
    nameLink: {
      color: '#0066cc',
      textDecoration: 'none',
      fontWeight: '500',
      fontSize: isMobile ? '16px' : '16px'
    },
    companyLink: {
      color: '#374151',
      textDecoration: 'none',
      fontSize: isMobile ? '15px' : '15px'
    },
    // 手機版卡片樣式
    mobileCard: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      marginBottom: '1rem',
      border: '1px solid #e2e8f0'
    },
    mobileCardHeader: {
      background: 'linear-gradient(135deg, #0066cc 0%, #004499 100%)',
      color: 'white',
      borderRadius: '12px 12px 0 0',
      padding: '0.75rem 1rem',
      fontSize: '16px',
      fontWeight: '600'
    }
  };

  // 手機版社友卡片組件
  const renderMobileEmployeeCard = (employee, index) => (
    <Card key={employee.e_id} style={customStyles.mobileCard}>
      <div style={customStyles.mobileCardHeader}>
        <Row className="align-items-center">
          <Col xs={8}>
            <Badge bg="light" text="dark" className="me-2" style={{ fontSize: '12px' }}>
              #{index + 1}
            </Badge>
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{employee.e_name}</span>
          </Col>
          <Col xs={4} className="text-end">
            <Badge 
              bg="primary" 
              style={{ 
                backgroundColor: '#0066cc', 
                fontSize: '12px',
                fontWeight: 'normal'
              }}
            >
              {employee.store_name || '未知'}
            </Badge>
          </Col>
        </Row>
      </div>
      <Card.Body className="p-3">
        <Row>
          <Col xs={6}>
            <div className="mb-2">
              <span className="text-muted" style={{ fontSize: '14px' }}>社職：</span>
              <span style={{ fontSize: '15px', color: '#374151' }}>
                {employee.position_title || '無'}
              </span>
            </div>
            <div className="mb-2">
              <span className="text-muted" style={{ fontSize: '14px' }}>電話：</span>
              <a 
                href={`tel:${employee.e_mobile}`}
                style={customStyles.nameLink}
                className="text-decoration-none"
              >
                📞 {employee.e_mobile || '無'}
              </a>
            </div>
            <div className="mb-2">
              <span className="text-muted" style={{ fontSize: '14px' }}>公司：</span>
              {employee.e_comp_web ? (
                <a
                  href={employee.e_comp_web.startsWith('http') ? employee.e_comp_web : `https://${employee.e_comp_web}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={customStyles.companyLink}
                  className="text-decoration-none d-block"
                >
                  <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#374151' }}>
                    {employee.e_comp_name || '查看公司網址'}
                  </div>
                  <span className="text-muted" style={{ fontSize: '13px' }}>
                    🌐 {employee.e_comp_web}
                  </span>
                </a>
              ) : (
                <div style={{ fontSize: '15px' }}>
                  <div style={{ fontWeight: 'bold', color: '#374151' }}>
                    {employee.e_comp_name || '無'}
                  </div>
                  <span className="text-muted" style={{ fontSize: '13px' }}>無官網資訊</span>
                </div>
              )}
            </div>
            <div>
              <span className="text-muted" style={{ fontSize: '14px' }}>職稱：</span>
              <span style={{ fontSize: '15px', color: '#6b7280' }}>
                {employee.e_comp_position || '無'}
              </span>
            </div>
          </Col>
          <Col xs={6} className="text-end">
            <LazyLoad height={144} offset={100} once>
              <img
                src={
                  employee.e_photo
                  ? `/uploads/member/${employee.e_photo}`
                  : '/uploads/member/default.jpg'
                }
                alt={`${MEMBER_TERM}相片`}
                style={customStyles.employeePhoto}
                onError={(e) => {
                  e.target.src = '/uploads/member/default.jpg';
                }}
              />
            </LazyLoad>
          </Col>
        </Row>
        {/* 營業項目跨越整個卡片寬度 */}
        <Row className="mt-3">
          <Col xs={12}>
            <span className="text-muted" style={{ fontSize: '14px' }}>營業項目：</span>
            <div style={{ marginTop: '4px' }}>
              <span 
                style={{ 
                  backgroundColor: employee.sql_title4 ? '#10b981' : '#9ca3af',
                  color: 'white',
                  fontSize: '12px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  display: 'block',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  lineHeight: '1.4',
                  width: '100%',
                  textAlign: 'center'
                }}
              >
                {employee.sql_title4 || '未分類'}
              </span>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  return (
    <div style={customStyles.pageContainer}>
      <Container fluid>
        {/* 頁面標題卡片 */}
        <Card style={customStyles.headerCard} className="mb-4">
          <Card.Body className="text-center" style={{ padding: isMobile ? '1.5rem 1rem' : '2rem' }}>
            <h2 className="mb-2 fw-bold" style={{ fontSize: isMobile ? '20px' : '28px' }}>
              🏢 {`${process.env.REACT_APP_CLUB_SHORT_NAME || 'TWVN'} 全國${MEMBER_TERM}通訊錄`}
            </h2>
            <p className="mb-0 opacity-90" style={{ fontSize: isMobile ? '13px' : '16px' }}>
              
            </p>
          </Card.Body>
        </Card>

        

        {/* 搜尋區域 */}
        <Card className="mb-4" style={{ border: 'none', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)' }}>
          <Card.Body style={{ padding: isMobile ? '1rem' : '1.5rem' }}>
            <Row>
              {/* 搜尋框 */}
              <Col md={8} xs={12} className="mb-3 mb-md-0">
                <InputGroup size={isMobile ? "md" : "lg"}>
                  <InputGroup.Text style={{ 
                    backgroundColor: '#f8fafc', 
                    border: 'none',
                    fontSize: isMobile ? '16px' : '18px'
                  }}>
                    🔍
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={isMobile ? "搜尋姓名、社別、營業項目..." : `搜尋${MEMBER_TERM}姓名、社別、公司名稱、營業項目...`}
                    value={filter}
                    onChange={handleFilterChange}
                    style={customStyles.searchInput}
                  />
                </InputGroup>
              </Col>
              
              {/* 社別過濾 */}
              <Col md={4} xs={12}>
                <InputGroup size={isMobile ? "md" : "lg"}>
                  <InputGroup.Text style={{ 
                    backgroundColor: '#f8fafc', 
                    border: 'none',
                    fontSize: isMobile ? '16px' : '18px'
                  }}>
                    🏪
                  </InputGroup.Text>
                  <Form.Select
                    value={storeFilter}
                    onChange={handleStoreFilterChange}
                    style={{
                      borderRadius: isMobile ? '15px' : '25px',
                      border: '2px solid #e2e8f0',
                      fontSize: isMobile ? '16px' : '18px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="">所有社別</option>
                    {allStores.map(store => (
                      <option key={store} value={store}>{store}</option>
                    ))}
                  </Form.Select>
                </InputGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* 主要內容區域 - 根據設備類型顯示不同佈局 */}
        {isMobile ? (
          // 手機版卡片佈局
          <div>
            {employees.map((employee, index) => (
              <div key={employee.e_id} ref={index === employees.length - 1 ? lastEmployeeRef : null}>
                {renderMobileEmployeeCard(employee, index)}
              </div>
            ))}
            
            {/* 手機版載入狀態 */}
            {loading && (
              <Card className="text-center py-4 mb-3" style={{ border: '1px solid #e2e8f0' }}>
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-center">
                    <div 
                      className="spinner-border text-primary me-2" 
                      role="status"
                      style={{ width: '1.2rem', height: '1.2rem' }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span style={{ color: '#0066cc', fontWeight: '500', fontSize: '16px' }}>
                      載入更多{MEMBER_TERM}資料中...
                    </span>
                  </div>
                </Card.Body>
              </Card>
            )}
            
            {/* 手機版無更多資料 */}
            {!hasMore && employees.length > 0 && (
              <Card className="text-center py-4 mb-3" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Card.Body>
                  <div style={{ color: '#6b7280', fontSize: '16px' }}>
                    📋 已顯示全部 {employees.length} 位{MEMBER_TERM}資料
                  </div>
                </Card.Body>
              </Card>
            )}
            
            {/* 手機版無搜尋結果 */}
            {!loading && employees.length === 0 && filter && (
              <Card className="text-center py-5 mb-3" style={{ border: '1px solid #e2e8f0' }}>
                <Card.Body>
                  <div style={{ color: '#6b7280' }}>
                    <div style={{ fontSize: '36px', marginBottom: '1rem' }}>🔍</div>
                    <h6>找不到符合條件的{MEMBER_TERM}</h6>
                    <p style={{ fontSize: '15px', marginBottom: 0 }}>請嘗試修改搜尋關鍵字或清空搜尋條件</p>
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>
        ) : (
          // 桌面版表格佈局
          <div style={customStyles.tableContainer}>
            <Table hover responsive className="mb-0" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr style={customStyles.tableHeader}>
                  <th style={{ width: '5%', cursor: 'default' }}>序號</th>
                  <th style={{ width: '12%', cursor: 'default' }}>照片</th>
                  <th style={{ width: '11%', cursor: 'pointer' }} onClick={() => handleSort('store_name')}>
                    社別 {sortConfig.key === 'store_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ width: '11%', cursor: 'pointer' }} onClick={() => handleSort('position_title')}>
                    社職 {sortConfig.key === 'position_title' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ width: '12%', cursor: 'pointer' }} onClick={() => handleSort('e_name')}>
                    姓名 {sortConfig.key === 'e_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ width: '21%', cursor: 'pointer' }} onClick={() => handleSort('e_comp_name')}>
                    公司名稱 {sortConfig.key === 'e_comp_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ width: '16%', cursor: 'pointer' }} onClick={() => handleSort('e_comp_position')}>
                    公司職稱 {sortConfig.key === 'e_comp_position' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ width: '12%', cursor: 'pointer' }} onClick={() => handleSort('sql_title4')}>
                    營業項目 {sortConfig.key === 'sql_title4' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee, index) => (
                  <tr 
                    key={employee.e_id} 
                    ref={index === employees.length - 1 ? lastEmployeeRef : null}
                    style={customStyles.tableRow}
                    onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = '#f8fafc'}
                    onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = 'transparent'}
                  >
                    <td className="text-center py-3">
                      <Badge bg="light" text="dark" style={{ fontSize: '14px' }}>
                        {index + 1}
                      </Badge>
                    </td>
                    <td className="text-center py-3">
                      <LazyLoad height={96} offset={100} once>
                        <img
                          src={
                            employee.e_photo
                            ? `/uploads/member/${employee.e_photo}`
                            : '/uploads/member/default.jpg'
                          }
                          alt={`${MEMBER_TERM}相片`}
                          style={customStyles.employeePhoto}
                          onError={(e) => {
                            e.target.src = '/uploads/member/default.jpg';
                          }}
                        />
                      </LazyLoad>
                    </td>
                    <td className="py-3">
                      <Badge 
                        bg="primary" 
                        style={{ 
                          backgroundColor: '#0066cc', 
                          fontSize: '13px',
                          fontWeight: 'normal'
                        }}
                      >
                        {employee.store_name || '未知'}
                      </Badge>
                    </td>
                    <td className="py-3" style={{ color: '#6b7280', fontSize: '16px' }}>
                      {employee.position_title || '無'}
                    </td>
                    <td className="py-3">
                      <a 
                        href={`tel:${employee.e_mobile}`}
                        style={customStyles.nameLink}
                        className="fw-bold"
                      >
                        {employee.e_name}
                      </a>
                      <br />
                      <span className="text-muted" style={{ fontSize: '14px' }}>
                        📞 {employee.e_mobile || '無'}
                      </span>
                    </td>

                    <td className="py-3">
                      {employee.e_comp_web ? (
                        <a
                          href={employee.e_comp_web.startsWith('http') ? employee.e_comp_web : `https://${employee.e_comp_web}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={customStyles.companyLink}
                          className="text-decoration-none"
                        >
                          <div className="fw-bold" style={{ color: '#374151', fontSize: '15px' }}>
                            {employee.e_comp_name || '查看公司網址'}
                          </div>
                          <span className="text-muted" style={{ fontSize: '13px' }}>
                            🌐 {employee.e_comp_web}
                          </span>
                        </a>
                      ) : (
                        <div>
                          <div className="fw-bold" style={{ color: '#374151', fontSize: '15px' }}>
                            {employee.e_comp_name || '無'}
                          </div>
                          <span className="text-muted" style={{ fontSize: '13px' }}>無官網資訊</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3" style={{ color: '#6b7280', fontSize: '16px' }}>
                      {employee.e_comp_position || '無'}
                    </td>
                    <td className="py-3">
                      <span 
                        style={{ 
                          backgroundColor: employee.sql_title4 ? '#10b981' : '#9ca3af',
                          color: 'white',
                          fontSize: '13px',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          display: 'inline-block',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: '1.4',
                          maxWidth: '100%'
                        }}
                      >
                        {employee.sql_title4 || '未分類'}
                      </span>
                    </td>
                  </tr>
                ))}
                
                {/* 桌面版載入狀態 */}
                {loading && (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <div className="d-flex align-items-center justify-content-center">
                        <div 
                          className="spinner-border text-primary me-2" 
                          role="status"
                          style={{ width: '1.5rem', height: '1.5rem' }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span style={{ color: '#0066cc', fontWeight: '500' }}>載入更多{MEMBER_TERM}資料中...</span>
                      </div>
                    </td>
                  </tr>
                )}
                
                {/* 桌面版無更多資料 */}
                {!hasMore && employees.length > 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <div style={{ color: '#6b7280' }}>
                        <hr style={{ margin: '1rem 0', borderColor: '#e2e8f0' }} />
                        📋 已顯示全部 {employees.length} 位{MEMBER_TERM}資料
                      </div>
                    </td>
                  </tr>
                )}
                
                {/* 桌面版無搜尋結果 */}
                {!loading && employees.length === 0 && filter && (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <div style={{ color: '#6b7280' }}>
                        <div style={{ fontSize: '48px', marginBottom: '1rem' }}>🔍</div>
                        <h5>找不到符合條件的{MEMBER_TERM}</h5>
                        <p>請嘗試修改搜尋關鍵字或清空搜尋條件</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Container>
    </div>
  );
};

export default AllEmployeeTable;
